<template>
  <Dialog
    :visible="visible"
    :title="$t(`${namespace}.updateEmployeeSupervisorTitle`)"
    :isLoading="isLoading"
    max-width="638"
    @close="close()"
  >
    <div v-if="visible">
      <form novalidate @submit.prevent="batchUpdate">
        <v-card-text class="dialog__body">
          <p class="pb-2">{{ $t(`${namespace}.updateEmployeeSupervisorMsg`) }}</p>
          <p><strong>{{ $t(`${namespace}.updateEmployeeSupervisorStep1`) }}</strong></p>

          <v-radio-group
            v-model="radioGroup"
            v-validate="{ required: true }"
            :error-messages="errors.collect($t('Common.employees'))"
            :data-vv-name="$t('Common.employees')"
            required
          >
            <v-radio :label="$t(`${namespace}.allEmployees`)" value="allEmployees"></v-radio>
            <v-radio :label="$t(`${namespace}.selectEmployeesByName`)" value="perName"></v-radio>
            <v-radio :label="$t(`${namespace}.selectEmployeesById`)" value="perId"></v-radio>
          </v-radio-group>

          <AutoComplete
            v-if="radioGroup === 'perName'"
            kind="employee"
            :data="employeeIds"
            :companyId="company.id"
            :placeholder="$t(`${namespace}.searchEmployees`)"
            :isValid="radioGroup === 'perName' && isEmployeeValid"
            :required="radioGroup === 'perName'"
            multiple
            @input="updateEmployee"
          />

          <FormTextField
            v-if="radioGroup === 'perId'"
            v-model="employeesIdsField"
            :name="$t(`${namespace}.fieldIdsName`)"
            :placeholder="$t(`${namespace}.fieldIdsPlaceholder`)"
            :disabled="isLoading"
            :v-validate="{ required: true }"
            ref="name"
            @blur="normalizeIDs"
          />

          <p><strong>{{ $t(`${namespace}.updateEmployeeSupervisorStep2`) }}</strong></p>

          <AutoComplete
            kind="employee"
            :companyId="company.id"
            :placeholder="$t(`${namespace}.searchSupervisor`)"
            :isValid="isSupervisorValid"
            required
            @input="updateSupervisor"
          />
        </v-card-text>

        <v-divider />

        <v-card-actions class="dialog__footer">
          <v-spacer />

          <a
            v-if="!isLoading"
            class="mr-4"
            @click.prevent="close()"
          >
            {{ $t(`${namespace}.cancel`) }}
          </a>

          <Button
            type="submit"
            :loading="isLoading"
            :disabled="isLoading"
          >
            {{ $t(`${namespace}.updateSupervisor`) }}
          </Button>
        </v-card-actions>
      </form>
    </div>
  </Dialog>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { isValidArray, isValidObject, catchError } from 'corp-front-vue-commons/src/utils/functions'
import AutoComplete from 'components/AutoComplete'

export default {
  name: 'SupervisorBulkUpdate',
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  components: {
    AutoComplete
  },
  computed: {
    ...mapState({
      company: ({ company }) => company.selectedCompany
    })
  },
  data () {
    return {
      namespace: 'Components.ApplyToAllDialog',
      radioGroup: null,
      isLoading: false,

      // custom validation
      isEmployeeValid: true,
      isSupervisorValid: true,

      // fields
      supervisorId: null,
      employeeIds: [],
      employeesIdsField: ''
    }
  },
  methods: {
    ...mapActions({
      showAlert: 'snackbar/show'
    }),

    isValidArray (val) {
      return isValidArray(val)
    },

    isValidObject (val) {
      return isValidObject(val)
    },

    setLoadingValue (boolean) {
      this.isLoading = boolean
    },

    resetEmployeeList () {
      this.employeeIds = []
      this.employeesIdsField = ''
    },

    resetAllFields () {
      this.resetEmployeeList()
      this.radioGroup = null
      this.supervisorId = null
      this.isEmployeeValid = true
      this.isSupervisorValid = true
    },

    forceFieldValidation (ref, selector) {
      const $field = this.$refs[ref]?.$el

      if ($field) {
        $field.querySelector(selector).focus()
        $field.querySelector(selector).blur()
      }
    },

    normalizeIDs () {
      if (this.employeesIdsField.trim().length) {
        this.employeesIdsField = this.employeesIdsField
          .replace(/[^\d,]/g, '')
          .replace(/,+/g, ',')

        this.employeeIds = this.employeesIdsField.split(',').map(Number).filter(Boolean)
      }
    },

    close (reload = false) {
      this.$emit('close', reload)
    },

    updateEmployee (val) {
      if (!this.isValidArray(val)) {
        this.employeeIds = []
        this.radioGroup !== 'allEmployees' && (this.isEmployeeValid = false)
        return
      }

      this.employeeIds = val
      this.radioGroup !== 'allEmployees' && (this.isEmployeeValid = true)
    },

    updateSupervisor (val) {
      if (!this.isValidObject(val)) {
        this.supervisorId = null
        this.isSupervisorValid = false
        return
      }

      this.supervisorId = val.id
      this.isSupervisorValid = true
    },

    async batchUpdate () {
      if (this.radioGroup !== 'allEmployees' && !this.employeeIds.length) {
        this.isEmployeeValid = false
      }

      if (!this.supervisorId) {
        this.isSupervisorValid = false
      }

      this.forceFieldValidation('name', 'input')

      try {
        const isValid = await this.$validator.validateAll()

        if (
          (this.radioGroup !== 'allEmployees' && !this.employeeIds.length) ||
          !this.supervisorId
        ) {
          return
        }

        if (isValid) {
          this.setLoadingValue(true)

          let ids = null

          if (this.radioGroup === 'perName') {
            const employeeIds = this.employeeIds.map(item => item.id)
            ids = this.employeeIds.length ? employeeIds : null
          } else {
            ids = this.employeeIds
          }

          await this.$apiService.employee.batchUpdate(
            this.company.id,
            { budget: null },
            { budgetDate: null },
            { budgetPeriod: 'monthly' },
            { credit: null },
            { useBudget: null },
            { useRideCredit: null },
            { categoryIds: null },
            { supervisorId: this.supervisorId },
            { thirdPartyWebCallPermission: null },
            { webCallPermission: null },
            { ids }
          )

          this.showAlert({
            type: 'success',
            message: this.$t(`${this.namespace}.updateEmployeeSupervisorSuccess`)
          })

          this.resetAllFields()
          this.close(true)
          this.setLoadingValue(false)
        }
      } catch (error) {
        this.showAlert({
          type: 'error',
          message: this.$t(`${this.namespace}.error`),
          timeout: 6000
        })

        this.setLoadingValue(false)
        catchError(error)
      }
    }
  },
  watch: {
    radioGroup () {
      this.resetEmployeeList()
    },

    employeesIdsField (val) {
      if (!val) this.resetEmployeeList()
    },

    visible (val) {
      // fix validation error when close dialog
      this.$validator._paused = !val

      this.resetAllFields()
      this.setLoadingValue(false)

      val && this.$ga.page('/dialog/colaboradores/atualizar-supervisor-em-massa')
    }
  }
}
</script>
